import { handleHtmlData } from './utils';

const parseSection = rawHtml => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${rawHtml || ''}</div>`, 'text/html');
  const container = doc.body.firstElementChild;
  const sectionEl = container?.querySelector?.('section');
  return { doc, container, sectionEl };
};

// Read a section's data-type and its first inline <style> in a single parse.
const readSection = rawHtml => {
  const { container, sectionEl } = parseSection(rawHtml);
  return {
    type: container?.querySelector?.('section[data-type]')?.getAttribute?.('data-type') || null,
    styleOuterHtml: sectionEl?.querySelector?.('style')?.outerHTML || null,
  };
};

// Replace a section's inline <style>; pass a falsy value to simply remove it.
const setSectionStyle = (rawHtml, styleOuterHtml) => {
  const { doc, container, sectionEl } = parseSection(rawHtml);
  if (!sectionEl) return rawHtml;

  sectionEl.querySelectorAll('style').forEach(el => el.remove());
  if (styleOuterHtml) {
    const tmp = doc.createElement('div');
    tmp.innerHTML = styleOuterHtml;
    const styleEl = tmp.firstElementChild;
    if (styleEl) sectionEl.insertBefore(styleEl, sectionEl.firstChild);
  }
  return container.innerHTML;
};

export const resolveSectionInsertIndex = (sections, { section, position } = {}) => {
  const list = Array.isArray(sections) ? sections : [];

  if (!section) {
    const firstVisible = list.find(s => !s?.hidden);
    if (!firstVisible) return 0;
    const idx = list.findIndex(s => s.id === firstVisible.id);
    return idx >= 0 ? idx : 0;
  }

  const realIndex = list.findIndex(s => s.id === section.id);
  if (realIndex < 0) return -1;
  return position === 'below' ? realIndex + 1 : realIndex;
};

const getSectionDataType = section => (section?.html ? readSection(section.html).type : null) || section?.type || null;

// Enforce one <style> per data-type, owned by the most-recently-added section.
const normalizeSectionStyles = sections => {
  const list = Array.isArray(sections) ? sections : [];
  if (!list.length) return list;

  const ownerByType = new Map();
  for (let i = list.length - 1; i >= 0; i--) {
    if (!list[i]?.html) continue;
    const type = getSectionDataType(list[i]);
    if (!type || ownerByType.has(type)) continue;
    const { styleOuterHtml } = readSection(list[i].html);
    if (styleOuterHtml) ownerByType.set(type, { index: i, styleOuterHtml });
  }

  return list.map((section, index) => {
    if (!section?.html) return section;
    const owner = ownerByType.get(getSectionDataType(section));
    if (!owner) return section;
    const style = index === owner.index ? owner.styleOuterHtml : null;
    return { ...section, html: setSectionStyle(section.html, style) };
  });
};

export const loadSectionsFromHtml = html => normalizeSectionStyles(handleHtmlData(html));

const generateSectionId = () =>
  `section-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;

// Give every section a fresh id (and re-stamp its data-section attribute) so
// copy/paste never collides with the ids already present in the editor.
export const regenerateSectionIds = sections => {
  const list = Array.isArray(sections) ? sections : [];

  return list.map(section => {
    if (!section) return section;
    const newId = generateSectionId();

    if (!section.html) return { ...section, id: newId };

    const { container, sectionEl } = parseSection(section.html);
    const target = container?.querySelector?.('[data-section]') || sectionEl;
    if (!target) return { ...section, id: newId };

    target.setAttribute('data-section', newId);
    return { ...section, id: newId, html: container.innerHTML };
  });
};

export const insertSections = (sections, templates, { atIndex } = {}) => {
  const list = Array.isArray(sections) ? sections : [];
  const items = (Array.isArray(templates) ? templates : [templates]).filter(Boolean);
  if (!items.length) return normalizeSectionStyles(list);

  // Drop styles of the incoming types from existing sections so the new ones win.
  const incomingTypes = new Set(items.map(getSectionDataType).filter(Boolean));
  const next = list.map(section =>
    incomingTypes.has(getSectionDataType(section))
      ? { ...section, html: setSectionStyle(section.html, null) }
      : section,
  );

  if (typeof atIndex === 'number' && atIndex >= 0 && items.length === 1) {
    next.splice(atIndex, 0, items[0]);
  } else {
    next.push(...items);
  }

  return normalizeSectionStyles(next);
};

export const deleteSection = (sections, section) => {
  const list = Array.isArray(sections) ? sections : [];
  const { type, styleOuterHtml: deletingStyle } = readSection(section?.html);
  const deletingType = type || section?.type || null;
  const next = list.filter(item => item.id !== section.id);

  // If the removed section owned the style, hand it to the last same-type section.
  if (deletingType && deletingStyle) {
    let lastIndex = -1;
    next.forEach((s, i) => {
      if (getSectionDataType(s) === deletingType) lastIndex = i;
    });
    if (lastIndex >= 0) {
      next[lastIndex] = { ...next[lastIndex], html: setSectionStyle(next[lastIndex].html, deletingStyle) };
    }
  }

  return normalizeSectionStyles(next);
};
